/**
 * TileBadge - the little app-icon-style number pill Bobbie asked for:
 * "when something new has happened can they get a small number badge next
 * to it like an app would?" Rose pill, badge-ink number, pinned to the
 * top-right corner of a tile's icon (the parent supplies position:
 * relative). Caps at "9+", renders nothing at 0 or while a count is not
 * yet known - a missing badge is always safer than a wrong number.
 */

import { waitingCountLabel } from '../lib/staffWaitingOnYouCore';

export const TILE_BADGE_MAX = 9;

/** "3", or "9+" once the count outgrows a one-digit pill. */
export function tileBadgeLabel(count: number): string {
  const n = Math.floor(count);
  return n > TILE_BADGE_MAX ? `${TILE_BADGE_MAX}+` : String(n);
}

/**
 * `incomplete` is the one case a zero may not hide the pill: a check behind
 * the count FAILED, so it is only what survived (see WaitingCountState in
 * lib/staffWaitingOnYouCore.ts; a full list is not a failure). It draws "?"
 * rather than nothing, and "3+" rather than "3", so a tile never reads as all
 * clear on a count nobody finished.
 */
export function TileBadge({ count, incomplete = false }: { count: number; incomplete?: boolean }) {
  const safe = Number.isFinite(count) ? count : 0;
  if (safe < 1 && !incomplete) return null;
  const n = Math.max(0, Math.floor(safe));
  const label = incomplete ? waitingCountLabel(n, true, TILE_BADGE_MAX) : tileBadgeLabel(n);
  return (
    <span
      role="img"
      aria-label={incomplete
        ? (n > 0 ? `At least ${n} waiting` : 'Could not check what is waiting')
        : `${n} waiting`}
      style={{
        position: 'absolute',
        top: -6,
        right: -6,
        minWidth: 20,
        height: 20,
        padding: '0 5px',
        borderRadius: 999,
        background: 'var(--rose)',
        color: 'var(--tile-badge-ink)',
        /* Paper ring separates the pill from the lime icon wash beneath it,
           the same way OS home screens ring their badges. */
        border: '2px solid var(--paper)',
        boxSizing: 'content-box',
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
        boxShadow: '0 1px 4px rgba(10,10,10,0.25)',
      }}
    >
      {label}
    </span>
  );
}

export default TileBadge;
